import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./style.css";
import { useDispatch, useSelector, useStore } from "react-redux";
import { LuMinus, LuPlus } from "react-icons/lu";
import { translateMessage } from "@delibrandev/utils";

import {
  setAlert,
  setOptimisticCartProductQuantity,
  setOptimisticOrderProductQuantity,
} from "../../reducers/userConfigSlice";
import { enqueueProductChange, flushAllProductChanges } from "../../services/productSyncQueue";

const getPid = (data) =>
  data?.productId ?? data?.id ?? data?.Product?.productId ?? data?.Product?.id ?? null;

const normalizeItems = (value) => {
  const items = Array.isArray(value) ? value : value == null || value === "" ? [] : [value];
  return items.map(String).filter(Boolean).sort().join(",");
};

const buildLineKey = (item) =>
  [
    String(getPid(item) ?? ""),
    String(item?.variantId ?? ""),
    normalizeItems(item?.itemIds),
    String(item?.note ?? "").trim(),
  ].join("|");

export const CartActions = ({
  callback = () => null,
  callbackAction,
  data,
  showAdd = true,
  showRemove = true,
  showCounter = true,
  addLabel = "",
  renderAdd,
  confirmAddition = false,
  onConfirmationState,
}) => {
  const dispatch = useDispatch();
  const providerStore = useStore();
  const confirmation = useRef(false), queued = useRef(null), alive = useRef(true);
  const [confirmBusy, setConfirmBusy] = useState(false), [confirmError, setConfirmError] = useState("");
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const cart = useSelector((state) => state.aplicationConfig.userConfig.cart);
  const order = useSelector((state) => state.aplicationConfig.userConfig.order);
  const client = useSelector((state) => state.aplicationConfig.userConfig.client);
  const selectedShippingMethodId = useSelector(
    (state) => state.aplicationConfig.userConfig.selectedShippingMethodId,
  );
  const shippingMethods = useSelector(
    (state) => state.aplicationConfig.corporationData.pointMethods.shipping,
  );

  const isOrder = Boolean(order?.orderId);
  const productId = getPid(data);
  const productName = data?.Product?.shortName || data?.shortName || data?.Product?.name || data?.name || "producto";
  const lineKey = useMemo(() => buildLineKey(data), [data]);
  const selectedShippingMethod = (Array.isArray(shippingMethods) ? shippingMethods : []).find(
    (method) => String(method?.shippingMethodId) === String(selectedShippingMethodId),
  );
  const isDefaultClient = String(client?.fullname || "").trim().toLowerCase() === "cliente contado";
  const deliveryClientRequired =
    Boolean(selectedShippingMethod?.homeDeliveryIsActive) && (!client?.customerId || isDefaultClient);
  const afterAction = typeof callbackAction === "function" ? callbackAction : callback;

  const products = isOrder
    ? Array.isArray(order?.products)
      ? order.products
      : []
    : Array.isArray(cart?.ShoppingCartProducts)
      ? cart.ShoppingCartProducts
      : [];
  const existingItem = products.find((item) => buildLineKey(item) === lineKey);

  const applyChange = useCallback(
    async (delta) => {
      if (confirmAddition && confirmation.current) return;
      if (!productId) {
        dispatch(setAlert(translateMessage("Producto no válido")));
        return;
      }

      if (isOrder) {
        if (!order?.orderId) {
          dispatch(setAlert(translateMessage("Pedido no válido")));
          return;
        }
      } else {
        if (deliveryClientRequired) {
          dispatch(setAlert(translateMessage("Selecciona un cliente para el envío a domicilio")));
          return;
        }
        if (!client?.customerId) {
          dispatch(setAlert(translateMessage("Cliente no válido")));
          return;
        }
      }

      if (confirmAddition) {
        confirmation.current = true;
        setConfirmBusy(true);
        setConfirmError("");
        onConfirmationState?.({ locked: true, busy: true });
      }
      try {
        if (!confirmAddition || !queued.current) {
          const source = existingItem ?? data;
          const currentQuantity = Number(existingItem?.quantity || 0);
          if (delta < 0 && currentQuantity <= 0) return;
          const desiredQuantity = enqueueProductChange({
            resourceType: isOrder ? "order" : "cart",
            resourceId: isOrder ? order.orderId : cart?.shoppingCartId ?? null,
            customerId: client?.customerId, lineKey, source, currentQuantity, delta,
            serverExists: Boolean(existingItem),
          });
          dispatch((isOrder ? setOptimisticOrderProductQuantity : setOptimisticCartProductQuantity)({source, quantity: desiredQuantity}));
          if (confirmAddition) queued.current = {quantity: desiredQuantity, orderId: order?.orderId, cartId: cart?.shoppingCartId, customerId: client?.customerId};
        }
        if (confirmAddition) {
          if (!(await flushAllProductChanges())) throw Error("No se ha confirmado la actualización. Reintenta la comprobación de la misma solicitud o revisa la comanda.");
          if (!alive.current) return;
          const current = providerStore.getState().aplicationConfig.userConfig;
          const target = queued.current;
          const sameResource = isOrder ? current.order?.orderId === target.orderId : current.client?.customerId === target.customerId && (!target.cartId || current.cart?.shoppingCartId === target.cartId);
          const lines = isOrder ? current.order?.products : current.cart?.ShoppingCartProducts;
          const confirmed = (lines || []).find(item => buildLineKey(item) === lineKey);
          if (!sameResource || Number(confirmed?.quantity) !== target.quantity) throw Error("No se ha podido confirmar la línea guardada. Revisa la comanda antes de añadir de nuevo.");
          callback();
        } else if (isOrder) afterAction(delta);
        else callback();
      } catch (failure) {
        if (confirmAddition && alive.current) setConfirmError(failure.message || "No se pudo confirmar el producto.");
        else if (!confirmAddition) dispatch(setAlert(translateMessage("No se pudo actualizar el producto")));
      } finally {
        if (confirmAddition) {
          confirmation.current = false;
          if (alive.current) { setConfirmBusy(false); onConfirmationState?.({locked: Boolean(queued.current), busy: false}); }
        }
      }
    },
    [
      afterAction,
      confirmAddition, onConfirmationState, providerStore,
      callback,
      cart?.shoppingCartId,
      client?.customerId,
      data,
      deliveryClientRequired,
      dispatch,
      existingItem,
      isOrder,
      lineKey,
      order?.orderId,
      productId,
    ],
  );

  if (confirmAddition) return <div className="configuredProductSubmit">
    {confirmError && <p role="alert">{confirmError}</p>}
    <button type="button" className="cartQuantityControl buttonCatalog" disabled={confirmBusy} aria-label={queued.current ? `Reintentar comprobación de ${productName}` : `Añadir una unidad de ${productName}`} onClick={() => applyChange(1)}><LuPlus aria-hidden="true" /><span>{confirmBusy ? "Añadiendo…" : queued.current ? "Reintentar comprobación" : addLabel || "Añadir al pedido"}</span></button>
  </div>;

  if (typeof renderAdd === "function") return renderAdd(() => applyChange(1));

  return (
    <>
      {showRemove && <button type="button" className="cartQuantityControl" aria-label={`Quitar una unidad de ${productName}`} disabled={!existingItem?.quantity} title={!existingItem?.quantity ? "No hay unidades que quitar" : undefined} onClick={() => applyChange(-1)}><LuMinus aria-hidden="true"/></button>}
      {showCounter && <div className="product-name">{existingItem?.quantity ?? 0}</div>}
      {showAdd && (
        <button type="button" className="cartQuantityControl buttonCatalog" aria-label={`Añadir una unidad de ${productName}`} onClick={() => applyChange(1)}><LuPlus aria-hidden="true"/>{addLabel && <span>{addLabel}</span>}</button>
      )}
    </>
  );
};
